import { useEffect, useState } from 'react'
import api from '../utils/api'

const BURGUNDY = '#8B1A1A'
const GOLD = '#B8860B'
const CARD_BG = '#111111'
const BORDER = '#1e1e1e'
const TEXT_DIM = '#c0c0c0'
const TEXT_MID = '#d8d8d8'
const TEXT_BRIGHT = '#f0f0f0'

const S = {
  root: {
    fontFamily: "'DM Sans', sans-serif",
    color: TEXT_BRIGHT,
    animation: 'fadeIn 0.4s ease forwards',
  },
  sectionLabel: {
    fontSize: '0.68rem',
    textTransform: 'uppercase',
    letterSpacing: '0.2em',
    color: GOLD,
    fontWeight: 600,
    marginBottom: '1.2rem',
  },
  statGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '1rem',
    marginBottom: '1.5rem',
  },
  statCard: {
    background: CARD_BG,
    border: `1px solid ${BORDER}`,
    borderTop: `3px solid ${BURGUNDY}`,
    borderRadius: '6px',
    padding: '1.4rem 1.2rem',
  },
  statValue: {
    fontSize: '2.1rem',
    fontWeight: 700,
    color: '#fff',
    lineHeight: 1.1,
    letterSpacing: '-0.02em',
    marginBottom: '0.35rem',
  },
  statLabel: {
    fontSize: '0.75rem',
    color: TEXT_DIM,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    marginBottom: '0.4rem',
  },
  statNote: {
    fontSize: '0.72rem',
    color: '#d0d0d0',
    fontStyle: 'italic',
  },
  twoCol: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '1rem',
    marginBottom: '1rem',
  },
  card: {
    background: CARD_BG,
    border: `1px solid ${BORDER}`,
    borderRadius: '6px',
    padding: '1.4rem',
  },
  cardTitle: {
    fontSize: '0.72rem',
    textTransform: 'uppercase',
    letterSpacing: '0.18em',
    color: GOLD,
    fontWeight: 600,
    marginBottom: '1rem',
    paddingBottom: '0.6rem',
    borderBottom: `1px solid ${BORDER}`,
  },
  findingsList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
  },
  findingItem: {
    display: 'flex',
    gap: '0.7rem',
    alignItems: 'flex-start',
    marginBottom: '0.8rem',
    fontSize: '0.85rem',
    color: TEXT_MID,
    lineHeight: 1.5,
  },
  findingDot: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    background: BURGUNDY,
    marginTop: '7px',
    flexShrink: 0,
  },
  findingHighlight: {
    color: '#e0e0e0',
    fontWeight: 600,
  },
  pulseWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  pulseItem: {
    background: '#0d0d0d',
    border: `1px solid ${BORDER}`,
    borderRadius: '4px',
    padding: '0.8rem 1rem',
    fontSize: '0.8rem',
    color: '#bbb',
    fontStyle: 'italic',
  },
  aboutCard: {
    background: '#0d0d0d',
    border: `1px solid #1a1a1a`,
    borderLeft: `3px solid ${GOLD}`,
    borderRadius: '6px',
    padding: '1.2rem 1.4rem',
    fontSize: '0.8rem',
    color: '#d0d0d0',
    lineHeight: 1.7,
  },
  aboutTitle: {
    fontSize: '0.68rem',
    textTransform: 'uppercase',
    letterSpacing: '0.18em',
    color: '#bbb',
    marginBottom: '0.5rem',
    fontWeight: 600,
  },
}

const STAT_CARDS = [
  {
    value: '3.4 BILLION',
    label: 'Fish Exported Annually',
    note: '~85% of total US menhaden catch',
  },
  {
    value: '$0.075',
    label: 'Avg. Price Per Fish',
    note: '7.5 cents — less than a penny per ounce',
  },
  {
    value: '$250M',
    label: 'Annual Export Value',
    note: 'Estimated 2024 (USITC data)',
  },
  {
    value: '100%',
    label: 'Foreign-Owned Processing',
    note: 'Omega Protein acquired by Cooke Inc. (Canada)',
  },
]

const FINDINGS = [
  { highlight: 'China receives 48%', rest: ' of all US menhaden exports by volume — dominant single destination.' },
  { highlight: '$17–$18/hr wages', rest: ' reported at Omega Protein Reedville facility — near poverty threshold for the region.' },
  { highlight: 'ASMFC quota unchanged', rest: ' since 2012 amendment. Independent scientists flagged stock concerns in 2023 review.' },
  { highlight: 'Reduction fishery', rest: ' converts whole fish into fishmeal and fish oil — no human food use.' },
  { highlight: 'Chesapeake Bay allocation', rest: ' capped at 51,000 metric tons since 2012 despite ongoing ecosystem concerns.' },
  { highlight: 'Omega Protein', rest: ' controls ~80% of US reduction capacity and has operated under multiple EPA consent orders.' },
  { highlight: 'Export data suggests', rest: ' unit value ~$1,300/MT fishmeal, consistent with global commodity pricing.' },
  { highlight: 'No mandatory bycatch reporting', rest: ' for the Atlantic reduction fleet as of the latest ASMFC addendum.' },
]

function PulseItem() {
  return (
    <div style={{ ...S.pulseItem, display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
      <span
        style={{
          display: 'inline-block',
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          background: '#2a2a2a',
          animation: 'pulse 2s ease-in-out infinite',
          flexShrink: 0,
        }}
      />
      News feed loading...
    </div>
  )
}

export default function Overview() {
  const [recentNews, setRecentNews] = useState(null)

  useEffect(() => {
    api.get('/data/news?limit=4')
      .then(res => setRecentNews(res.data.articles || []))
      .catch(() => setRecentNews([]))
  }, [])

  return (
    <div style={S.root}>
      <div style={S.sectionLabel}>Intelligence Overview</div>

      {/* Stat cards */}
      <div style={S.statGrid}>
        {STAT_CARDS.map(c => (
          <div key={c.label} style={S.statCard}>
            <div style={S.statValue}>{c.value}</div>
            <div style={S.statLabel}>{c.label}</div>
            <div style={S.statNote}>{c.note}</div>
          </div>
        ))}
      </div>

      {/* Two column */}
      <div style={S.twoCol}>
        {/* Key Findings */}
        <div style={S.card}>
          <div style={S.cardTitle}>Key Findings</div>
          <ul style={S.findingsList}>
            {FINDINGS.map((f, i) => (
              <li key={i} style={S.findingItem}>
                <span style={S.findingDot} />
                <span>
                  <span style={S.findingHighlight}>{f.highlight}</span>
                  {f.rest}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* Recent Activity */}
        <div style={S.card}>
          <div style={S.cardTitle}>Recent Activity</div>
          <div style={S.pulseWrap}>
            {recentNews === null ? (
              <>
                <PulseItem />
                <PulseItem />
                <PulseItem />
                <PulseItem />
              </>
            ) : recentNews.length === 0 ? (
              <div style={{ ...S.pulseItem, fontStyle: 'italic', color: '#888' }}>No recent articles.</div>
            ) : (
              recentNews.map((article, i) => (
                <div key={article.id ?? article.url ?? i} style={{ ...S.pulseItem, fontStyle: 'normal', cursor: 'default' }}>
                  <div style={{ color: '#d0d0d0', fontWeight: 500, marginBottom: '0.2rem', lineHeight: 1.4 }}>
                    {article.title}
                  </div>
                  <div style={{ fontSize: '0.72rem', color: '#888' }}>
                    {article.source && <span>{article.source} · </span>}
                    {article.published_at
                      ? new Date(article.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                      : ''}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* About */}
      <div style={S.aboutCard}>
        <div style={S.aboutTitle}>About This Resource</div>
        This portal is an independent research compilation of publicly available data — government filings, ASMFC records,
        USITC trade databases, SEC/EDGAR disclosures, and published journalism. No proprietary or non-public information
        is included. Compiled for policy and advocacy research purposes. Last updated{' '}
        <span style={{ color: '#d8d8d8' }}>March 2026</span>.
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 1; }
        }
      `}</style>
    </div>
  )
}
